import { useEffect, useState } from 'react';
import { AppRouter } from './components/Router';
import { useTelegram } from './hooks/useTelegram';
import { useAuth } from './hooks/useAuth';
import { UnauthorizedScreen } from './components/UnauthorizedScreen';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LanguageProvider } from './i18n';
import { apiClient } from './services/api';
import './App.css';

function App() {
  const { tg, isReady, isTelegramEnv, colorScheme } = useTelegram();
  const { isLoading: isAuthLoading, isAuthenticated, user, retry: retryAuth } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check onboarding status when Telegram is ready and user is authenticated
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!isReady || !isAuthenticated) return;

      try {
        const status = await apiClient.getOnboardingStatus();
        setShowOnboarding(!status.onboarding_completed);
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Default to showing onboarding if we can't check
        // This ensures new users still see onboarding even if API is temporarily unavailable
        setShowOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [isReady, isAuthenticated]);

  // Apply Telegram theme
  useEffect(() => {
    if (tg?.themeParams) {
      const root = document.documentElement;
      const theme = tg.themeParams;
      
      if (theme.bg_color) {
        root.style.setProperty('--tg-theme-bg-color', theme.bg_color);
      }
      if (theme.text_color) {
        root.style.setProperty('--tg-theme-text-color', theme.text_color);
      }
      if (theme.hint_color) {
        root.style.setProperty('--tg-theme-hint-color', theme.hint_color);
      }
      if (theme.button_color) {
        root.style.setProperty('--tg-theme-button-color', theme.button_color);
      }
      if (theme.button_text_color) {
        root.style.setProperty('--tg-theme-button-text-color', theme.button_text_color);
      }
    }

    // Apply color scheme class
    document.body.className = colorScheme === 'dark' ? 'dark-theme' : 'light-theme';
  }, [tg, colorScheme]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  // Get user's language - prioritize selected_language, default to Russian (ignore Telegram language)
  const userLanguage = user?.selected_language || 'es';

  // Render content based on auth state
  const renderContent = () => {
    // Show loading screen while waiting for init or auth check
    if (!isReady || isAuthLoading) {
      return (
        <div className="app-loading">
          <div className="loading-content">
            <div className="loading-icon">🧘</div>
            <div className="loading-spinner"></div>
            <p>Loading SanaSivana...</p>
            <small>
              {!isReady ? (isTelegramEnv ? 'Connecting to Telegram...' : 'Loading...') : 'Verifying authentication...'}
            </small>
          </div>
        </div>
      );
    }

    // Show unauthorized screen if authentication failed
    if (!isAuthenticated) {
      return (
        <UnauthorizedScreen
          onRetry={retryAuth}
          isRetrying={isAuthLoading}
          onLoginSuccess={retryAuth}
        />
      );
    }

    // Show loading screen while checking onboarding
    if (loading) {
      return (
        <div className="app-loading">
          <div className="loading-content">
            <div className="loading-icon">🧘</div>
            <div className="loading-spinner"></div>
            <p>Loading SanaSivana...</p>
            <small>Checking your progress...</small>
          </div>
        </div>
      );
    }

    // Main app content
    return (
      <UserProvider user={user} isLoading={isAuthLoading || loading}>
        <NotificationProvider>
          <AppRouter 
            showOnboarding={showOnboarding}
            onOnboardingComplete={handleOnboardingComplete}
          />
        </NotificationProvider>
      </UserProvider>
    );
  };

  // Single LanguageProvider wrapping all content
  // userLanguage updates when user data loads, triggering language change
  return (
    <LanguageProvider userLanguage={userLanguage}>
      {renderContent()}
    </LanguageProvider>
  );
}

export default App;
